import { actions, feedback, feedbackV2, overlays } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useState } from 'react';

interface ModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  title: React.ReactNode;
  children: React.ReactNode;
  label: string;
  isDisabled?: boolean;
  isDanger?: boolean;
}
const { Modal } = overlays;
const { Button } = actions;
const DEFAULT_ERROR_MESSAGE = 'There has been an error. Please try again.';

const transformErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message || DEFAULT_ERROR_MESSAGE;
  }
  return DEFAULT_ERROR_MESSAGE;
};

/*
 * Confirmation modal component for user actions
 * Displays a modal with a title, content, and confirm/cancel buttons.
 * To display an error message in the modal, throw an error from the parent onConfirm handler and keep isOpen=true.
 * The modal will show the error message and remain open until the user cancels or the operation succeeds.
 */
export const ConfirmationModal = ({
  title,
  children,
  isOpen,
  isDisabled,
  onCancel,
  onConfirm,
  label,
  isDanger = false,
}: ModalProps): JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);
    try {
      await onConfirm();
    } catch (error: unknown) {
      setErrorMessage(transformErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, onConfirm]);

  useEffect(() => {
    if (!isOpen) {
      setErrorMessage('');
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onCancel} renderInPortal={true}>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        {errorMessage && (
          <div data-test-id="confirmation-modal-alert-error">
            <feedbackV2.Alert type="error" title={errorMessage} size={feedback.AlertSize.SMALL} />
          </div>
        )}
        {children}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel} mode="secondary" testId="cancel-btn" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          disabled={isDisabled || isSubmitting}
          onClick={handleConfirm}
          mode="primary"
          testId="confirm-btn"
          isDanger={isDanger}
        >
          {label}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
